import { SelectSimple } from '@advanza/input'
import { SelectSimpleChangeEvent } from '@advanza/input/interfaces/SelectSimpleChangeEvent'
import Card from 'components/Card'
import ServiceRequestsStatsGeoGraph from 'components/serviceInfo/ServiceRequestsStatsGeoGraph'
import ServiceRequestStatsCalendarChart from 'components/serviceInfo/ServiceRequestStatsCalendarChart'
import ServiceStatsContainer from 'containers/ServiceStatsContainer'
import { useEffect } from 'react'

interface RequestStats {
    nrReq: number
}

interface StatsOptions {
    days?: number
    isFetching?: boolean
}

interface ServiceStatsContainerProps {
    serviceId: number
    changeOptions: (serviceId: number, type: string, options: Record<string, unknown>) => void
    fetchStats: (serviceId: number, type: string) => void
    getStatsAndOptions: (type: string) => {
        stats: RequestStats | null
        options: StatsOptions
    }
}

const ServiceRequestStatsAll = ({
    serviceId,
    changeOptions,
    fetchStats,
    getStatsAndOptions,
}: ServiceStatsContainerProps) => {
    useEffect(() => {
        fetchStats(serviceId, 'requestStats')
    }, [serviceId, fetchStats])

    const _changeOptions = (options: Record<string, unknown>) => {
        changeOptions(serviceId, 'requestStats', { ...options, didInvalidate: true })
        fetchStats(serviceId, 'requestStats')
    }

    const onChangeDays = (e: SelectSimpleChangeEvent) => {
        _changeOptions({ days: e.target.value })
    }

    const { stats, options } = getStatsAndOptions('requestStats')
    if (!stats) {
        return null
    }
    const { isFetching } = options
    const days = options.days || 30
    const periodText = (() => {
        switch (days) {
            case 90:
                return '3 months'
            case 180:
                return '6 months'
            case 365:
                return '1 year'
            default:
                return `${days} days`
        }
    })()

    return (
        <div className="bg-white border border-grey-500 rounded-xl p-5 shadow-[0_1px_3px_rgba(0,0,0,0.05)] mb-6">
            <div className="flex items-start justify-between mb-4 flex-wrap gap-4">
                <div>
                    <h3 className="text-xl font-semibold text-bodyDark m-0 mb-1.5">
                        Request Stats - All Services
                    </h3>
                    <p className="text-[13px] text-grey-800 m-0 leading-[1.4] max-w-[500px]">
                        Total customer requests across all services with geographic distribution and
                        time-based trends
                    </p>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                    <SelectSimple
                        value={days}
                        onChange={onChangeDays}
                        className="select-stealth select-min select-wide"
                        options={[
                            { value: 7, name: '7 days' },
                            { value: 10, name: '10 days' },
                            { value: 30, name: '30 days' },
                            { value: 90, name: '3 months' },
                            { value: 180, name: '6 months' },
                            { value: 365, name: '1 year' },
                        ]}
                    />
                </div>
            </div>
            <div className="mb-5">
                <div className="bg-primary-600 rounded-xl p-6 text-white shadow-[0_4px_12px_rgba(82,126,239,0.3)]">
                    <div className="text-[13px] font-medium text-white/90 mb-2 uppercase tracking-[0.5px]">
                        Total Requests
                    </div>
                    <div className="text-[42px] font-bold text-white mb-3 leading-none">
                        {isFetching ? '..' : stats.nrReq}
                    </div>
                    <div className="text-md text-white/90">requests in the last {periodText}</div>
                </div>
            </div>
            <div className="grid grid-cols-1 gap-4 mt-4">
                <Card header="Requests" icon="assignment">
                    <ServiceRequestsStatsGeoGraph serviceId={serviceId} />
                </Card>
                <div className="bg-white border border-grey-500 rounded-xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
                    <ServiceRequestStatsCalendarChart serviceId={serviceId} />
                </div>
            </div>
        </div>
    )
}

export default ServiceStatsContainer(ServiceRequestStatsAll)
